import { getCurrencyFromRegion, getDurationFromRegion } from "@Components/LeadFormModal/utils";
import {
  getPricingCurrency,
  getPricingDuration,
  getPricingRangeFromRegion,
} from "@Pages/Search/components/SearchListItem/helper";
import { COUNTRIES_WITH_MONTHLY_DURATION, PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useMemo, useState } from "react";
import { FilterComponentProps } from "../../ActiveFilterComponent/types";

// Define Props type
type Props = {
  region: any;
  onApplyClick: any;
  dispatch: any;
} & FilterComponentProps<string | string[]>;

// Main hook logic
const useBudgetFilter = ({
  value,
  region,
  dispatch,
  onApplyClick,
  isInlineFilters = false,
  dynamiCount,
  minAvailablePrice,
  maxAvailablePrice,
}: Props) => {
  const priceDetails = useMemo(() => {
    const range_available_price = getPricingRangeFromRegion(region);
    const isMonthlyCountry = COUNTRIES_WITH_MONTHLY_DURATION.includes(
      region?.location?.country?.short_name,
    );

    const regionMinPrice = isMonthlyCountry
      ? region?.region_inventory_price_range?.min_available_price
      : region?.region_inventory_price_range?.min_weekly_price;

    const regionMaxPrice = isMonthlyCountry
      ? region?.region_inventory_price_range?.max_available_price
      : region?.region_inventory_price_range?.max_weekly_price;

    const min = Number(minAvailablePrice || regionMinPrice || range_available_price?.[0] || 0);
    const max = Number(maxAvailablePrice || regionMaxPrice || range_available_price?.[1] || 1000);

    const step = dynamiCount?.[0]?.end_price - dynamiCount?.[0]?.start_price;

    // Formatted duration
    const regionPricingDuration = getDurationFromRegion(region?.combined_name);
    const duration = getPricingDuration(regionPricingDuration);

    // Formatted currency
    const regionCurrency = getCurrencyFromRegion(region?.combined_name);
    const currency = getPricingCurrency(regionCurrency);

    return { min, max, step, duration, currency };
  }, [region, region?.combined_name, dynamiCount, minAvailablePrice, maxAvailablePrice]);

  const initValue1 = Number(value?.[0] || priceDetails.min);
  const initValue2 = Number(value?.[1] || priceDetails.max);
  const [value1, setValue1] = useState(initValue1);
  const [value2, setValue2] = useState(initValue2);

  const isPriceRangeSelected = priceDetails.min !== value1 || priceDetails.max !== value2;

  const handleOnChange = ([min, max]: [min: number, max: number]) => {
    const val1 = min + priceDetails.min;
    const val2 = max + priceDetails.min;
    setValue1(val1);
    setValue2(val2);
    if (isInlineFilters) {
      dispatch({
        type: "budget",
        payload: [
          String(Math.trunc(min + priceDetails.min)),
          String(Math.trunc(max + priceDetails.min)),
        ],
      });
    }
  };

  const handleOnDragEnd = (values?: [number, number]) => {
    // Use values from the slider event if provided (avoids stale state on direct click)
    const finalValue1 = values ? values[0] + priceDetails.min : value1;
    const finalValue2 = values ? values[1] + priceDetails.min : value2;
    setValue1(finalValue1);
    setValue2(finalValue2);
    dispatch({
      type: "budget",
      payload: [String(Math.trunc(finalValue1)), String(Math.trunc(finalValue2))],
    });
    if (typeof onApplyClick === "function") {
      // applies filters for desktop
      onApplyClick({ budget: [String(Math.trunc(finalValue1)), String(Math.trunc(finalValue2))] });
    }

    gtagEventEmitter({
      action: "price_range_filter_applied",
      category: PAGE.SEARCH,
      min_price: finalValue1,
      max_price: finalValue2,
      ...{ ...(isInlineFilters && { section: "Inline Filters" }) },
    });
  };

  const isInRange = (index: any, dynamiCountValue: any[]) => {
    const minRange = Math.abs(Math.floor((initValue1 - priceDetails.min) / priceDetails.step));
    const maxRange = Math.abs(
      (dynamiCountValue?.length || 50) -
        Math.floor((priceDetails.max - initValue2) / priceDetails.step),
    );

    return index < minRange || index > maxRange;
  };

  return {
    initValue1,
    initValue2,
    value1: Math.trunc(value1),
    value2: Math.trunc(value2),
    setValue1,
    setValue2,
    priceDetails,
    handleOnChange,
    handleOnDragEnd,
    isPriceRangeSelected,
    isInRange,
  };
};

export default useBudgetFilter;
